import { FilterItem } from '../types/notes';

export type ElementType = 'note' | 'card' | 'review';

interface TagExtractorConfig {
  notes?: {
    customTags?: boolean;
    dateTags?: boolean;
  };
  cards?: {
    customTags?: boolean;
    dateTags?: boolean;
  };
  reviews?: {
    customTags?: boolean;
    dateTags?: boolean;
  };
}

/**
 * Extracts tags from different types of elements (notes, cards, reviews, etc.) and combines them into a unified format
 * @param items The items to extract tags from
 * @param config Configuration for which element types and tag types to include
 * @returns Array of Tag objects with name and associated element types
 */
export const extractTags = (items: FilterItem[], config: TagExtractorConfig = {}) => {
  const tagMap = new Map<string, Set<ElementType>>();

  items.forEach(item => {
    if (item.type === 'note' && config.notes) {
      // Extract tags from notes (at root level)
      if (config.notes.customTags && item.custom_tags) {
        item.custom_tags.forEach(tag => {
          const types = tagMap.get(tag) || new Set();
          types.add('note');
          tagMap.set(tag, types);
        });
      }
      if (config.notes.dateTags && item.date_tags) {
        item.date_tags.forEach(tag => {
          const types = tagMap.get(tag) || new Set();
          types.add('note');
          tagMap.set(tag, types);
        });
      }
    } else if (item.type === 'card' && config.cards) {
      // Extract tags from cards (both root level and card_data)
      if (config.cards.customTags) {
        // Check root level custom tags
        if (item.custom_tags) {
          item.custom_tags.forEach(tag => {
            const types = tagMap.get(tag) || new Set();
            types.add('card');
            tagMap.set(tag, types);
          });
        }
        // Check card_data custom tags
        if (item.card_data?.custom_tags) {
          item.card_data.custom_tags.forEach(tag => {
            const types = tagMap.get(tag) || new Set();
            types.add('card');
            tagMap.set(tag, types);
          });
        }
      }
      if (config.cards.dateTags) {
        // Check root level date tags
        if (item.date_tags) {
          item.date_tags.forEach(tag => {
            const types = tagMap.get(tag) || new Set();
            types.add('card');
            tagMap.set(tag, types);
          });
        }
        // Check card_data date tags
        if (item.card_data?.date_tags) {
          item.card_data.date_tags.forEach(tag => {
            const types = tagMap.get(tag) || new Set();
            types.add('card');
            tagMap.set(tag, types);
          });
        }
      }
    } else if (item.type === 'review' && config.reviews) {
      // Extract tags from reviews
      if (config.reviews.customTags && item.custom_tags) {
        item.custom_tags.forEach(tag => {
          const types = tagMap.get(tag) || new Set();
          types.add('review');
          tagMap.set(tag, types);
        });
      }
      if (config.reviews.dateTags && item.date_tags) {
        item.date_tags.forEach(tag => {
          const types = tagMap.get(tag) || new Set();
          types.add('review');
          tagMap.set(tag, types);
        });
      }
    }
  });

  return Array.from(tagMap.entries())
    .map(([name, types]) => ({
      name,
      types: Array.from(types)
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}; 